import { CONTENT_BOTTOM, CONTENT_TOP } from './pdfLayoutConstants';

/**
 * A minimal layout tree that mirrors the drawable items of the PDF microservice.
 *
 * - `leaf`: an atomic item (text block, image, divider, row). The parent column inserts
 *   a page break before it when it does not fit in the remaining space (`Column.draw`).
 * - `column`: items flow sequentially and break across pages individually.
 * - `singlePage`: tries to keep all children on one page; if they do not fit in the
 *   remaining space it starts on a fresh page first (`SinglePageColumn`).
 * - `nextPage`: forces a page break unless already at the top of a fresh page.
 *
 * `Grid` and `Row` from the microservice are pre-flattened by the tree builder into a
 * `column` of `leaf` rows, so they need no dedicated node here.
 */
export type LayoutNode =
  | LeafNode
  | ColumnNode
  | SinglePageColumnNode
  | NextPageNode;

export interface LeafNode {
  kind: 'leaf';
  height: number;
  /** Dividers are dropped when they would be the first/last item after a page break. */
  isDivider?: boolean;
}

export interface ColumnNode {
  kind: 'column';
  children: LayoutNode[];
}

export interface SinglePageColumnNode {
  kind: 'singlePage';
  children: LayoutNode[];
}

export interface NextPageNode {
  kind: 'nextPage';
}

export const leaf = (height: number, isDivider = false): LeafNode => ({
  kind: 'leaf',
  height,
  isDivider,
});

export const divider = (height: number): LeafNode => leaf(height, true);

export const column = (children: LayoutNode[]): ColumnNode => ({
  kind: 'column',
  children,
});

export const singlePage = (
  children: LayoutNode[]
): SinglePageColumnNode => ({
  kind: 'singlePage',
  children,
});

export const nextPage = (): NextPageNode => ({ kind: 'nextPage' });

function isColumnLike(node: LayoutNode): boolean {
  return node.kind === 'column' || node.kind === 'singlePage';
}

/**
 * Sums node heights for fit checks. Dividers keep their full height here even
 * though drawColumn may drop them at page boundaries — the ±10% estimate band
 * tolerates that simplification.
 */
function nodeHeight(node: LayoutNode): number {
  switch (node.kind) {
    case 'leaf':
      return node.height;
    case 'nextPage':
      return 0;
    case 'column':
    case 'singlePage':
      return node.children.reduce((sum, child) => sum + nodeHeight(child), 0);
  }
}

interface Cursor {
  pages: number;
  y: number;
}

function availableHeight(cursor: Cursor): number {
  return CONTENT_BOTTOM - cursor.y;
}

function startNewPage(cursor: Cursor): void {
  cursor.pages += 1;
  cursor.y = CONTENT_TOP;
}

function isAtPageTop(cursor: Cursor): boolean {
  return cursor.y <= CONTENT_TOP;
}

function drawColumn(children: LayoutNode[], cursor: Cursor): void {
  children.forEach((child, index) => {
    if (!isColumnLike(child) && child.kind !== 'nextPage') {
      const height = nodeHeight(child);
      if (availableHeight(cursor) < height) {
        const isDivider = child.kind === 'leaf' && Boolean(child.isDivider);
        // A divider that does not fit is dropped rather than pushed to a new page.
        if (isDivider && index === children.length - 1) {
          return;
        }
        startNewPage(cursor);
        // Skip a divider that would otherwise lead a fresh page.
        if (isDivider) {
          return;
        }
      }
    }
    drawNode(child, cursor);
  });
}

function drawNode(node: LayoutNode, cursor: Cursor): void {
  switch (node.kind) {
    case 'leaf':
      cursor.y += node.height;
      return;
    case 'nextPage':
      if (!isAtPageTop(cursor)) {
        startNewPage(cursor);
      }
      return;
    case 'column':
      drawColumn(node.children, cursor);
      return;
    case 'singlePage': {
      const total = nodeHeight(node);
      if (!isAtPageTop(cursor) && availableHeight(cursor) < total) {
        startNewPage(cursor);
      }
      drawColumn(node.children, cursor);
      return;
    }
  }
}

/**
 * Replays the layout of `root` and returns the number of pages it occupies. The root is
 * treated as a top-level column (sequential flow with per-item page breaks).
 */
export function countLayoutPages(root: LayoutNode): number {
  const cursor: Cursor = { pages: 1, y: CONTENT_TOP };
  drawNode(
    root.kind === 'column' ? root : column([root]),
    cursor
  );
  return Math.max(1, cursor.pages);
}
